import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import { Calculate, Info, ViewWeek } from '@mui/icons-material';
import ResponsiveDiagram from './ResponsiveDiagram';

const BalusterSpacing: React.FC = () => {
  const [finishedWidth, setFinishedWidth] = useState('');
  const [balusterWidth, setBalusterWidth] = useState('');
  const [desiredSpace, setDesiredSpace] = useState('');
  const [centerToCenter, setCenterToCenter] = useState<string | null>(null);
  const [numberOfBalusters, setNumberOfBalusters] = useState<string | null>(null);
  const [calculatedSpace, setCalculatedSpace] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [calculatedInputs, setCalculatedInputs] = useState<string | null>(null);
  const currentInputs = JSON.stringify([finishedWidth, balusterWidth, desiredSpace]);
  const isStale = calculatedInputs !== null && calculatedInputs !== currentInputs;

  const handleCalculate = () => {
    if (!finishedWidth || !balusterWidth || !desiredSpace) {
      setShowError(true);
      return;
    }

    const finishedWidthNum = parseFloat(finishedWidth);
    const balusterWidthNum = parseFloat(balusterWidth);
    const desiredSpaceNum = parseFloat(desiredSpace);

    if (isNaN(finishedWidthNum) || isNaN(balusterWidthNum) || isNaN(desiredSpaceNum)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setCalculatedInputs(currentInputs);

    // Calculate number of spaces
    const numberOfSpaces = Math.round((finishedWidthNum + balusterWidthNum) / (desiredSpaceNum + balusterWidthNum));
    
    // Calculate number of balusters
    const balusters = numberOfSpaces - 1;
    
    // Calculate center to center spacing
    const centerToCenterSpacing = (finishedWidthNum + balusterWidthNum) / numberOfSpaces;
    const roundedCenterToCenter = Math.round(centerToCenterSpacing * 1000) / 1000;
    
    // Calculate actual space between balusters
    const actualSpace = centerToCenterSpacing - balusterWidthNum;
    const roundedActualSpace = Math.round(actualSpace * 1000) / 1000;

    setCenterToCenter(roundedCenterToCenter.toString());
    setNumberOfBalusters(balusters.toString());
    setCalculatedSpace(roundedActualSpace.toString());
  };

  const hasResults = centerToCenter && numberOfBalusters && calculatedSpace;

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Calculate Baluster Spacing
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find optimal spacing and count for balusters
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Input Form */}
          <Card component="form" noValidate onSubmit={(event) => { event.preventDefault(); handleCalculate(); }} elevation={3} sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Input Values
                </Typography>
              </Box>

              <Stack spacing={3}>
                {showError && (
                  <Alert severity="error">Enter a number in each highlighted field, then calculate again.</Alert>
                )}
                <TextField
                  label="Finished Width"
                  value={finishedWidth}
                  error={showError && (!finishedWidth || isNaN(parseFloat(finishedWidth)))}
                  onChange={(e) => setFinishedWidth(e.target.value)}
                  type="number"
                  inputProps={{ step: "any", inputMode: "decimal" }}
                  fullWidth
                  variant="outlined"
                  helperText="Total width of rail or opening"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                />
                <TextField
                  label="Baluster Width"
                  value={balusterWidth}
                  error={showError && (!balusterWidth || isNaN(parseFloat(balusterWidth)))}
                  onChange={(e) => setBalusterWidth(e.target.value)}
                  type="number"
                  inputProps={{ step: "any", inputMode: "decimal" }}
                  fullWidth
                  variant="outlined"
                  helperText="Width of each baluster"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                />
                <TextField
                  label="Desired Space"
                  value={desiredSpace}
                  error={showError && (!desiredSpace || isNaN(parseFloat(desiredSpace)))}
                  onChange={(e) => setDesiredSpace(e.target.value)}
                  type="number"
                  inputProps={{ step: "any", inputMode: "decimal" }}
                  fullWidth
                  variant="outlined"
                  helperText="Desired gap between balusters"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                />
                <Button 
                  variant="contained" 
                  type="submit"
                  fullWidth
                  size="large"
                  sx={{ 
                    mt: 2,
                    background: 'linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)',
                    boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)',
                    }
                  }}
                >
                  Calculate Spacing
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Results Display */}
          <Card component="section" aria-label="Calculation results" aria-live="polite" aria-atomic="true" elevation={3} sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <ViewWeek sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Results
                </Typography>
              </Box>

              {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Inputs changed. These results are from your previous calculation. Calculate again to update them.
                </Alert>
              )}

              {hasResults ? (
                <Stack spacing={3}>
                  <Box sx={{ p: 2, bgcolor: '#eef7f0', borderRadius: 2, border: '1px solid', borderColor: 'success.light' }}>
                    <Chip label="Center to Center" color="success" size="small" sx={{ mb: 1 }} />
                    <Typography variant="h4" component="p" sx={{ fontWeight: 600, color: 'success.dark' }}>
                      {centerToCenter}"
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Exact spacing between baluster centers
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: '#edf5fc', borderRadius: 2, border: '1px solid', borderColor: 'info.light' }}>
                    <Chip label="Number of Balusters" color="info" size="small" sx={{ mb: 1 }} />
                    <Typography variant="h4" component="p" sx={{ fontWeight: 600, color: 'info.dark' }}>
                      {numberOfBalusters}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total balusters needed
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: '#fff4df', borderRadius: 2, border: '1px solid', borderColor: 'warning.light' }}>
                    <Chip label="Actual Space" color="warning" size="small" sx={{ mb: 1 }} />
                    <Typography variant="h4" component="p" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                      {calculatedSpace}"
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Actual gap between balusters
                    </Typography>
                  </Box>

                  <Divider />
                  
                  <Alert severity="info" role="note" icon={<Info />}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Formula Used:
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Spaces = Round((Width + Baluster) ÷ (Space + Baluster))
                    </Typography>
                    <Typography variant="caption" display="block">
                      Balusters = Spaces - 1
                    </Typography>
                    <Typography variant="caption" display="block">
                      Center to Center = (Width + Baluster) ÷ Spaces
                    </Typography>
                    <Typography variant="caption" display="block">
                      Actual Space = Center to Center - Baluster Width
                    </Typography>
                  </Alert>
                </Stack>
              ) : (
                <Box sx={{ 
                  height: 300, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'grey.300'
                }}>
                  <Typography color="text.secondary">
                    Enter values to see results
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Reference Diagram */}
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Info sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Reference Diagram
              </Typography>
            </Box>
            <ResponsiveDiagram imageName="baluster" alt="Baluster spacing diagram" />
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
};

export default BalusterSpacing;